import {
  AttributeOptionsWithValue,
  GroupLevelAttribute,
  GroupLevelAttributeSpec,
  GroupLevelAttributeValue,
  GroupLevelAttributeValueStatus,
  GroupLevelAttributeWithSpec
} from '@/jbi-shared/types/jaas-group-level-attributes.types';
import { isTitleAttribute } from '@/jbi-shared/util/group-level-attributes.util';

/**
 * This method converts group level attributes with values to attribute options
 * @param attributes Group level attributes with values
 */
export function convertToAttributeOptionsWithValue(
  attributes: GroupLevelAttributeValue[]
): AttributeOptionsWithValue[] {
  return attributes.map((attr) => ({
    groupLevelAttributeId: attr.id!, // Assertion that id exists (ensure validation)
    order: attr.groupLevelAttributeSpec.order,
    isRequired: attr.groupLevelAttributeSpec.isRequired,
    mapId: attr.mapId,
    groupLevelAttributeSpecId: attr.groupLevelAttributeSpec.id,
    status: attr.mapStatus,
    value: attr.value
  }));
}

/**
 * Transforms either GroupLevelAttribute or GroupLevelAttributeWithSpec arrays into GroupLevelAttributeValue arrays.
 * This utility function handles both basic attributes and those with existing specs, preserving
 * special fields like mapId and mapStatus when available. For attributes without specs,
 * it creates default specs with sequential ordering.
 *
 * @template T - The type of attributes being transformed (GroupLevelAttribute or GroupLevelAttributeWithSpec)
 * @param isDefault - Whether the spec should be initialised as default GLA. Is false by default.
 * @param arrayItemsToTransform - Array of attributes to transform
 */
export function transformGroupLevelAttributePayload<
  T extends GroupLevelAttribute | GroupLevelAttributeWithSpec
>(
  arrayItemsToTransform: T[],
  isDefault: boolean = false,
  initializeAsDisabled: boolean = false
): GroupLevelAttributeValue[] {
  const transformedItems: GroupLevelAttributeValue[] = arrayItemsToTransform.map(
    (attribute, index) => {
      // Determine if the attribute already has specs
      const hasSpecs = 'groupLevelAttributeSpec' in attribute;

      // Create or use existing spec data
      const groupLevelAttributeSpec: GroupLevelAttributeSpec = hasSpecs
        ? (attribute as GroupLevelAttributeWithSpec).groupLevelAttributeSpec
        : {
            order: index + 1,
            isRequired: true,
            isDefault
          };

      // Preserve mapId and mapStatus if they exist
      const mapId =
        'mapId' in attribute
          ? (attribute as GroupLevelAttributeWithSpec).mapId
          : undefined;
      const mapStatus =
        'mapStatus' in attribute
          ? (attribute as GroupLevelAttributeWithSpec).mapStatus
          : GroupLevelAttributeValueStatus.ACTIVE;

      return {
        id: Number(attribute.id),
        label: attribute.label,
        option: attribute.option,
        attributeStatus: attribute.attributeStatus,
        groupLevelAttributeType: attribute.groupLevelAttributeType,
        groupLevelAttributeSpec,
        value: attribute.option,
        mapId,
        mapStatus: mapStatus as GroupLevelAttributeValueStatus,
        isTitle: isTitleAttribute(attribute),
        isHidden: false,
        isDisabled: initializeAsDisabled,
        hasDuplicationError: false,
        hasFieldError: false // Fields should not throw validation error on load
      };
    }
  );

  return transformedItems;
}
